import { useState } from "react";
import { toast } from "react-toastify";
import { Sparkles, X, Loader2 } from "lucide-react";
import Portal from "../common/Portal";
import api from "../../api";

// Getting Started -> "Add a sample patient" (backend organization/sample_data.py):
// a fictional patient with two past sessions and every tab filled in, so a new
// doctor can see what goes where. Nothing is sent to anyone.
const INCLUDED = [
  "Profile, background and emergency contact",
  "Two past sessions with full notes (one signed, one left for you to sign)",
  "Risk assessments, PHQ-9 and GAD-7 scores with a trend",
  "Treatment plan with goals and the patient's check-ins",
  "Case formulation and a safety plan",
  "Homework with the patient's answers, mood check-ins",
  "Follow-ups and a short message thread",
];

export default function SamplePatientModal({ doctors = [], onClose, onDone }) {
  const [doctorId, setDoctorId] = useState(doctors[0]?.user_id || "");
  const [saving, setSaving] = useState(false);

  const add = async () => {
    setSaving(true);
    try {
      const res = await api.addSampleData(doctorId || undefined);
      toast.success("Sample patient added. Open her from Patients.");
      onDone(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.error || "Couldn't add the sample patient.");
      setSaving(false);
    }
  };

  return (
    <Portal>
      <div className="fixed inset-0 z-[1000] bg-black/40 flex items-center justify-center p-4">
        <div role="dialog" aria-modal="true" aria-labelledby="sample-title" className="w-full max-w-lg rounded-2xl bg-white shadow-xl overflow-hidden">
          <div className="flex items-start gap-3 px-5 py-4 bg-gradient-to-r from-primary-50 to-primary-100/60 border-b border-primary-100">
            <Sparkles className="w-5 h-5 text-primary-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <h2 id="sample-title" className="font-semibold text-secondary-900">Add a sample patient?</h2>
              <p className="text-sm text-secondary-600 mt-0.5">
                Ananya Rao (Sample), 29, a team lead with anxiety from workplace pressure. Everything is filled in so you can see what goes where.
              </p>
            </div>
            <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded-lg text-secondary-400 hover:text-secondary-700 hover:bg-white/60">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="px-5 py-4 space-y-4">
            <div>
              <p className="text-sm font-medium text-secondary-800 mb-1.5">What you'll find</p>
              <ul className="text-sm text-secondary-600 space-y-1 list-disc pl-5">
                {INCLUDED.map((x) => <li key={x}>{x}</li>)}
              </ul>
            </div>
            {doctors.length > 1 && (
              <label className="block normal-case font-sans tracking-normal text-sm text-secondary-800">
                <span className="font-medium">Whose sessions should these be?</span>
                <select value={doctorId} onChange={(e) => setDoctorId(e.target.value)}
                  className="mt-1.5 w-full rounded-lg border border-secondary-300 px-3 py-2 text-sm">
                  {doctors.map((d) => <option key={d.user_id} value={d.user_id}>{d.name}</option>)}
                </select>
              </label>
            )}
            <p className="text-xs text-secondary-500">
              It's fictional: no messages or alerts are sent, it doesn't count towards your setup progress, and you can remove it any time from Getting Started.
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-secondary-700 hover:bg-secondary-100">Not now</button>
              <button type="button" onClick={add} disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                {saving && <Loader2 className="w-4 h-4 animate-spin" />} Add sample patient
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
